import { useQuery } from "@tanstack/react-query"
import {
  generatePerdidaGananciaExcel,
  getPerdidaGanancianByFilters,
} from "api/PerdidaGanancia.service"
import axios from "axios"
import Spinner from "common/components/Spinner/"
import { useDownloadFileContext } from "common/context/DownloadFileProvider"
import BalanceTable from "features/Contabilidad/components/BalanceTable"
import { Stats, StatsProps } from "features/Contabilidad/components/Stats/Stats"
import SubMenuHeader from "features/Contabilidad/components/SubmenuHeader"
import {
  InformesCommonProps,
  MODES_INFORME,
  PERIOD_OPTIONS,
  formatInformesStorage,
  onTransformArrayData,
  transformMonthArray,
} from "features/Contabilidad/contants"
import {
  DATA_KEYS_PERDIDAS_GANANCIAS,
  TIPO_INFORMES_PERDIDA_GANANCIA,
} from "features/Contabilidad/pages/PerdidasGanancias/constants"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { frontendCurrency } from "utils/formatCurrency"
import { backendDateFormat } from "utils/formatDate"

const PerdidasGanancias = () => {
  const { t } = useTranslation()
  const { handleDirectDownloadExcel } = useDownloadFileContext()
  const [statsData, setStatsData] = useState<StatsProps>({
    resultado: "-",
    margen: "-",
    ventas: "-",
  })

  const [informesFilters, setInformesFilters] = useState<InformesCommonProps>(
    formatInformesStorage()
  )

  useEffect(() => {
    sessionStorage.setItem(
      "informesFilters",
      JSON.stringify({
        ...informesFilters,
        dateFilter:
          informesFilters?.dateFilter === undefined
            ? undefined
            : [
                informesFilters?.dateFilter?.[0],
                informesFilters?.dateFilter?.[1] ?? null,
              ],
        searchValue: informesFilters?.searchValue,
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [informesFilters])

  const perdidasGananciasToken = useRef(axios.CancelToken.source())

  const statsToken = useRef(axios.CancelToken.source())
  const { data: informe, isLoading: informeLoading } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      "getPerdidasGanancias",
      informesFilters?.perdidasGanancias.firstDropdownValue,
      informesFilters?.perdidasGanancias.secondDropdownValue,
      informesFilters?.dateFilter?.[0],
      informesFilters?.dateFilter?.[1],
      informesFilters.searchValue,
      informesFilters?.SoloCuentasConSaldo,
    ],
    queryFn: () => {
      perdidasGananciasToken.current?.cancel()
      perdidasGananciasToken.current = axios.CancelToken.source()
      return getPerdidaGanancianByFilters(
        {
          EsMensual: informesFilters?.perdidasGanancias.firstDropdownValue,
          Tipo: informesFilters?.perdidasGanancias.secondDropdownValue,
          FechaInicioMovimiento: informesFilters?.dateFilter?.[0]
            ? backendDateFormat(new Date(informesFilters?.dateFilter?.[0]))
            : undefined,
          FechaFinMovimiento: informesFilters?.dateFilter?.[1]
            ? backendDateFormat(new Date(informesFilters?.dateFilter?.[1]))
            : undefined,
          Keyword:
            informesFilters.searchValue !== ""
              ? informesFilters.searchValue
              : undefined,
          SoloCuentasConSaldo: informesFilters?.SoloCuentasConSaldo,
        },
        perdidasGananciasToken.current.token
      )
    },
    refetchOnWindowFocus: false,
    retry: false,
  })

  const { data: stats, isLoading: statsLoading } = useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      "getPerdidasGananciasStats",
      informesFilters?.dateFilter?.[0],
      informesFilters?.dateFilter?.[1],
    ],
    queryFn: () => {
      statsToken.current?.cancel()
      statsToken.current = axios.CancelToken.source()
      return getPerdidaGanancianByFilters(
        {
          Tipo: 4,
          EsMensual: false,
          FechaInicioMovimiento: informesFilters?.dateFilter?.[0]
            ? backendDateFormat(new Date(informesFilters?.dateFilter?.[0]))
            : undefined,
          FechaFinMovimiento: informesFilters?.dateFilter?.[1]
            ? backendDateFormat(new Date(informesFilters?.dateFilter?.[1]))
            : undefined,
        },
        statsToken.current.token
      )
    },
    refetchOnWindowFocus: false,
    retry: false,
  })

  const [isLoadingExcel, setIsLoadingExcel] = useState(false)
  const handleDownLoadExcel = async () => {
    setIsLoadingExcel(true)
    const archivo = await generatePerdidaGananciaExcel({
      EsMensual: informesFilters?.perdidasGanancias.firstDropdownValue,
      Tipo: informesFilters?.perdidasGanancias.secondDropdownValue,
      FechaInicioMovimiento: informesFilters?.dateFilter?.[0]
        ? backendDateFormat(new Date(informesFilters?.dateFilter?.[0]))
        : undefined,
      FechaFinMovimiento: informesFilters?.dateFilter?.[1]
        ? backendDateFormat(new Date(informesFilters?.dateFilter?.[1]))
        : undefined,
      Keyword:
        informesFilters.searchValue !== ""
          ? informesFilters.searchValue
          : undefined,
      SoloCuentasConSaldo: informesFilters?.SoloCuentasConSaldo,
    })
    if (archivo) {
      handleDirectDownloadExcel(archivo, "PerdidasGanancias.xls")
    }
    setIsLoadingExcel(false)
  }

  useEffect(() => {
    setStatsData({
      resultado: frontendCurrency(
        Number(
          stats?.items?.find((i) => i.description === "Resultado del ejercicio")
            ?.dataColumns?.[0] ?? 0
        )
      ),
      margen: frontendCurrency(
        Number(
          stats?.items?.find(
            (i) => i.description === "1. Importe neto de la cifra de negocios"
          )?.dataColumns?.[0] ?? 0
        ) -
          Math.abs(
            Number(
              stats?.items?.find(
                (i) => i.description === "4. Aprovisionamientos"
              )?.dataColumns?.[0] ?? 0
            )
          )
      ),
      ventas: frontendCurrency(
        Number(
          stats?.items?.find(
            (i) => i.description === "1. Importe neto de la cifra de negocios"
          )?.dataColumns?.[0] ?? 0
        )
      ),
    })
  }, [informesFilters.dateFilter, stats?.items])

  return (
    <section className='!tw-h-[calc(100%-150px)] tw-relative tw-bg-white'>
      <SubMenuHeader
        title={`${t("informes.selectedTab.headerProfitLoss")}`}
        mode={MODES_INFORME.PERDIDAS_GANANCIAS}
        informesFilters={informesFilters}
        setInformesFilters={setInformesFilters}
        handleDownload={() => handleDownLoadExcel()}
        secondOptions={TIPO_INFORMES_PERDIDA_GANANCIA(t)}
        secondPlaceholder={undefined}
        firstOptions={PERIOD_OPTIONS(t)}
        firstPlaceholder={undefined}
        isLoadingExcel={isLoadingExcel}
      />
      <div className='tw-flex tw-w-full tw-max-w-full'>
        {!informeLoading ? (
          <div className='tw-flex-1 tw-min-w-0 !tw-h-[calc(100dvh-300px)]'>
            <BalanceTable
              months={transformMonthArray(informe?.tableColumns)}
              period
              onTransformArrayData={onTransformArrayData(informe?.items)}
              dataKeySetup={DATA_KEYS_PERDIDAS_GANANCIAS}
            />
          </div>
        ) : (
          <Spinner />
        )}
        <div className='tw-w-fit tw-min-w-60 tw-shrink-0 tw-h-[calc(100dvh-300px)] tw-pt-6 tw-border-l tw-bg-white tw-flex tw-justify-end tw-overflow-y-auto tw-overflow-x-hidden'>
          <Stats
            resultado={statsData.resultado}
            margen={statsData.margen}
            ventas={statsData.ventas}
            loading={statsLoading}
          />
        </div>
      </div>
    </section>
  )
}

export default PerdidasGanancias
